import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardMedia, 
  CardActions, 
  IconButton, 
  CircularProgress, 
  Alert,
  Container
} from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon, Google as GoogleIcon } from '@mui/icons-material';
import { sponsorService } from '../../firebase/services';
import { googleDriveService } from '../../firebase/googleDrive';
import MediaUploader from '../components/MediaUploader';
import { getGoogleDriveMediaUrl } from '../utils/getGoogleDriveMediaUrl';
import SmartImage from '../utils/SmartImage';
export default function SponsorManager() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingLoading, setAddingLoading] = useState(false);
  const [isDriveLogged, setIsDriveLogged] = useState(false);
  const [driveAlert, setDriveAlert] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  // Checar status do Google Drive ao montar (apenas verificação, sem popup automático)
  useEffect(() => {
    const checkDriveStatus = async () => {
      try {
        // Apenas verificar se já está autenticado, sem tentar autenticação
        if (googleDriveService?.isAuthenticated && typeof googleDriveService.isAuthenticated === 'function') {
          const isAuth = googleDriveService.isAuthenticated();
          setIsDriveLogged(isAuth);
          
          if (isAuth) {
            console.log('✅ Google Drive já está autenticado');
          } else {
            console.log('ℹ️ Google Drive não está conectado, aguardando interação do usuário');
          }
        } else {
          console.log('ℹ️ Google Drive service não disponível');
          setIsDriveLogged(false);
        }
      } catch (error) {
        console.error('❌ Erro ao verificar status do Google Drive:', error);
        setIsDriveLogged(false);
      }
    };

    checkDriveStatus();
  }, []);

  useEffect(() => {
    const loadSponsors = async () => {
      try {
        setLoading(true);
        const sponsorsData = await sponsorService.getAllSponsors();
        setSponsors(sponsorsData);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar patrocinadores:', err);
        setError('Falha ao carregar os patrocinadores. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadSponsors();
  }, []);

  // Função para login Google Drive
  const handleDriveLogin = async () => {
    if (googleDriveService?.authenticate) {
      try {
        await googleDriveService.authenticate(true); // true = forçar autenticação interativa
        setIsDriveLogged(googleDriveService.isAuthenticated ? googleDriveService.isAuthenticated() : true);
        console.log('✅ Login manual do Google Drive realizado com sucesso');
        setDriveAlert('');
      } catch (err) {
        console.error('❌ Falha no login manual:', err);
        alert('Falha ao fazer login no Google Drive. Tente novamente.');
      }
    }
  };

  const handleFileSelect = (file) => {
    // Validate file type - apenas imagens
    const isImage = file.type.startsWith('image/');
    
    if (!isImage) {
      setError('Por favor, selecione apenas arquivos de imagem válidos (JPG, PNG, etc.).');
      return;
    }
    
    // Verificar o tamanho do arquivo (limite de 5MB para ícones)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      setError('O arquivo é muito grande. Por favor, selecione uma imagem menor que 5MB.');
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    
    setSelectedFile(file);
    setFilePreview(previewUrl);
    setError(null);
  };

  const removeSelectedFile = () => {
    if (filePreview && filePreview.startsWith('blob:')) {
      URL.revokeObjectURL(filePreview);
    }
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleAddSponsor = async () => {
    // Verifica se o Google Drive está logado
    if (!isDriveLogged) {
      setDriveAlert('Você precisa conectar ao Google Drive antes de adicionar um patrocinador.');
      return;
    }

    if (!selectedFile) {
      setError('Por favor, selecione uma imagem do patrocinador.');
      return;
    }

    try {
      setAddingLoading(true);
      const addedSponsor = await sponsorService.addSponsor(selectedFile);
      setSponsors([...sponsors, addedSponsor]);
      
      // Limpar seleção
      removeSelectedFile();
      setError(null);
      setDriveAlert('');
      
      window.dispatchEvent(new CustomEvent('sponsorsUpdated'));
    } catch (err) {
      console.error('Erro ao adicionar patrocinador:', err);
      setError('Falha ao adicionar o patrocinador. Por favor, tente novamente.');
    } finally {
      setAddingLoading(false);
    }
  };

  const handleRemoveSponsor = async (sponsor, index) => {
    try {
      await sponsorService.deleteSponsor(sponsor.id, sponsor.driveFileId);
      setSponsors(sponsors.filter((_, i) => i !== index));
      
      window.dispatchEvent(new CustomEvent('sponsorsUpdated'));
    } catch (err) {
      console.error('Erro ao remover patrocinador:', err);
      setError('Falha ao remover o patrocinador. Por favor, tente novamente.');
    }
  };

  return (
    <Container sx={{ py: 3 }}>
      <Typography 
        variant="h4" 
        sx={{
          fontFamily: 'Atlan',
          fontWeight: 'bold',
          marginBottom: 3,
          textAlign: 'center',
          color: '#0b2951'
        }}
      >
        Gerenciar Patrocinadores
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ marginBottom: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {driveAlert && (
        <Alert 
          severity="warning" 
          sx={{ marginBottom: 3 }}
          onClose={() => setDriveAlert('')}
        >
          {driveAlert}
        </Alert>
      )}

      {/* Seção de login Google Drive */}
      {!isDriveLogged && (
        <Card sx={{ p: 3, mb: 3, backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" gutterBottom>
            Conectar ao Google Drive
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Para adicionar ícones de patrocinadores, você precisa estar conectado ao Google Drive.
          </Typography>
          <Button
            variant="contained"
            onClick={handleDriveLogin}
            startIcon={<GoogleIcon />}
            sx={{
              fontFamily: 'Atlan',
              backgroundColor: '#db4437',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#c23321',
              },
            }}
          >
            Conectar ao Google Drive
          </Button>
        </Card>
      )}

      {/* Seção de adicionar novo patrocinador */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Adicionar Novo Patrocinador
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecione uma imagem do ícone/logo do patrocinador (máximo 5MB).
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <MediaUploader
            onFileSelect={handleFileSelect}
            acceptedTypes="image/*"
            maxSizeMB={5}
            label="Selecionar Ícone do Patrocinador"
          />
        </Box>

        {filePreview && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="subtitle2" gutterBottom>
              Prévia da imagem:
            </Typography>
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <CardMedia
                component="img"
                sx={{
                  maxWidth: 200,
                  maxHeight: 200,
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  padding: 1
                }}
                image={filePreview}
                alt="Prévia"
              />
              <IconButton
                onClick={removeSelectedFile}
                sx={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                  },
                }}
                size="small"
              >
                <DeleteIcon color="error" />
              </IconButton>
            </Box>
          </Box>
        )}

        <Button
          variant="contained"
          onClick={handleAddSponsor}
          startIcon={addingLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
          disabled={addingLoading || !selectedFile || !isDriveLogged}
          sx={{
            fontFamily: 'Atlan',
            backgroundColor: '#11407cff',
            color: '#fff',
            boxShadow: '0 2px 8px 0 rgba(11,64,124,0.10)',
            borderRadius: 2,
            paddingX: 3,
            paddingY: 1.2,
            letterSpacing: 1,
            transition: 'background 0.2s, box-shadow 0.2s',
            '&:hover': {
              backgroundColor: '#0a2347',
              boxShadow: '0 4px 16px 0 rgba(11,64,124,0.18)',
            },
            minWidth: 140
          }}
        >
          {addingLoading ? 'Adicionando...' : 'Adicionar Patrocinador'}
        </Button>
      </Card>

      {/* Seção de patrocinadores atuais */}
      <Card sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Patrocinadores Atuais
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={40} />
          </Box>
        ) : sponsors.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            Nenhum patrocinador adicionado ainda.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {sponsors.map((sponsor, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={sponsor.id}>
                <Card sx={{ 
                  position: 'relative'
                }}>
                  <Box sx={{
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    p: 1,
                    overflow: 'hidden'
                  }}>
                    <SmartImage
                      src={getGoogleDriveMediaUrl(
                        sponsor.imageUrl,
                        sponsor.driveFileId,
                        sponsor.webViewLink,
                        sponsor.thumbnailUrl,
                        false
                      )}
                      alt="Ícone do Patrocinador"
                      driveFileId={sponsor.driveFileId}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        display: 'block'
                      }}
                    />
                  </Box>
                  <CardActions sx={{ justifyContent: 'center', p: 1 }}>
                    <IconButton
                      className="delete-button"
                      onClick={() => handleRemoveSponsor(sponsor, index)}
                      sx={{ 
                        transition: 'color 0.2s',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          color: '#ff0000',
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Card>
    </Container>
  );
}